import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  CreditCard,
  ExternalLink,
  Plus,
} from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/Card'
import { toast } from 'sonner'
import useAuthStore from '@/store/useAuthStore'
import useBillingStore, { type BillingBlock } from '@/store/useBillingStore'
import useGetTeams from '@/services/api/team/useGetTeams'
import useSwitchTeam from '@/services/api/team/useSwitchTeam'
import { useCreatePortalSession } from '@/services/api/billing/useBillingSessions'
import { wasToastedByInterceptor } from './format'
import EndedSubscriptionNotice from './EndedSubscriptionNotice'

const COPY: Record<number, { title: string; body: string }> = {
  0: {
    title: 'No subscription',
    body: "This space doesn't have a subscription yet.",
  },
  1: {
    title: 'Subscription ended',
    body: "This space's subscription has been cancelled or has expired.",
  },
  2: {
    title: 'Trial ended',
    body: 'Your free trial for this space has ended.',
  },
  3: {
    title: 'Payment failed',
    body: "A payment for this space couldn't be collected after several retries, so access is paused. Pay the open invoice to restore it straight away.",
  },
}

interface Team {
  id: number
  name: string
  is_owner?: boolean
  is_default?: boolean
}

export default function BillingBlockedScreen({
  block,
}: {
  block: BillingBlock
}) {
  const navigate = useNavigate()
  const clearBlock = useBillingStore((s) => s.clearBlock)
  const currentTeamId = useAuthStore((s) => s.currentTeamId)
  const { data: teams = [] } = useGetTeams()
  const { action: switchTeam } = useSwitchTeam()
  const portalSession = useCreatePortalSession()
  const [switchingTeamId, setSwitchingTeamId] = useState<number | null>(null)

  const currentTeam = (teams as Team[]).find((t) => t.id === currentTeamId)
  const isOwner = !!currentTeam?.is_owner
  const otherSpaces = (teams as Team[]).filter((t) => t.id !== currentTeamId)
  const copy = COPY[block.code] ?? COPY[1]

  // /billing is reachable while blocked (billing endpoints never 421), so
  // lift the block and let the plan picker take over.
  const choosePlan = () => {
    clearBlock()
    navigate('/billing')
  }

  const updateCard = async () => {
    try {
      const { url } = await portalSession.mutateAsync({
        teamId: currentTeamId,
        action: 'payment_method',
      })
      window.location.href = url
    } catch (error) {
      if (!wasToastedByInterceptor(error)) toast.error('Could not open Stripe')
    }
  }

  const isUnpaid = block.code === 3

  const goToSpace = async (teamId: number) => {
    // Guard against double-clicks / re-clicks while the switch (and the burst
    // of refetches it triggers) is still in flight — each extra click was
    // firing another full switch + invalidateQueries() cascade.
    if (switchingTeamId !== null) return

    setSwitchingTeamId(teamId)
    try {
      // No clearBlock(): the gate ignores a block from another space, and
      // clearing here would wipe the new space's own block if it is locked too.
      await switchTeam({ teamId })
    } catch {
      toast.error('Could not switch space')
      setSwitchingTeamId(null)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-full bg-yellow-100 text-yellow-700">
            <AlertTriangle className="size-5" />
          </span>
          <div>
            <h2 className="text-lg font-semibold">{copy.title}</h2>
            <p className="text-sm text-muted-foreground">
              {currentTeam?.name ?? 'This space'}
            </p>
          </div>
        </div>

        {/* Codes 1-2: ended for good — make "choose a new plan" unmistakable
            and warn that paying an old invoice won't bring it back. */}
        {isOwner && (block.code === 1 || block.code === 2) ? (
          <EndedSubscriptionNotice
            wasTrial={block.code === 2}
            className="mb-5"
          />
        ) : (
          <p className="mb-5 text-sm text-muted-foreground">{copy.body}</p>
        )}

        {isOwner && isUnpaid ? (
          <div className="space-y-2">
            {block.payInvoiceUrl && (
              <a
                href={block.payInvoiceUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(buttonVariants(), 'w-full')}
              >
                Pay invoice
                <ExternalLink className="ml-2 size-4" />
              </a>
            )}
            <Button
              className="w-full"
              variant={block.payInvoiceUrl ? 'outline' : 'default'}
              onClick={updateCard}
              disabled={portalSession.isPending}
            >
              <CreditCard className="mr-2 size-4" />
              {portalSession.isPending ? 'Opening…' : 'Update payment method'}
            </Button>
          </div>
        ) : isOwner ? (
          <Button className="w-full" onClick={choosePlan}>
            <CreditCard className="mr-2 size-4" />
            {block.code === 2
              ? 'Choose a plan to continue'
              : 'Choose a new plan'}
          </Button>
        ) : (
          <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
            Contact the owner of <strong>{currentTeam?.name}</strong> to resolve
            billing for this space.
          </p>
        )}

        {otherSpaces.length > 0 && (
          <div className="mt-5">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Switch to another space
            </p>
            <div className="space-y-1">
              {otherSpaces.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => goToSpace(team.id)}
                  disabled={switchingTeamId !== null}
                  className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span>{team.name}</span>
                  {switchingTeamId === team.id ? (
                    <span className="text-xs text-muted-foreground">
                      Switching…
                    </span>
                  ) : (
                    <ArrowRight className="size-4 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          className="mt-4 w-full"
          onClick={() => navigate('/create-space')}
          disabled={switchingTeamId !== null}
        >
          <Plus className="mr-2 size-4" />
          Create a new space
        </Button>
      </Card>
    </div>
  )
}
